import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { EmailTrigger } from '../../../../enum/email.trigger';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AdminService } from '../../../../service/admin.service';
import { Event } from '../../../../model/event';
import { EventsService } from '../../../../service/events.service';
import { NotificationService } from '../../../../service/notification.service';
import { ActivatedRoute } from '@angular/router';
import { EmailTemplatesEditDirective } from 'src/app/class/email-templates-edit';
import { SelectOption } from '../../../../model/select.option';
import { UserNotification } from '../../../../model/user.notification';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { plainToClass } from 'class-transformer';
import { ImportEmailTemplatesDialogComponent } from 'src/app/component/import-email-templates-dialog/import-email-templates-dialog.component';
import { EmailTemplateEditDialogComponent } from 'src/app/component/email-template-edit-dialog/email-template-edit-dialog.component';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';
import { trigger } from '@angular/animations';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { User } from 'src/app/model/user';

export class TriggerData {
  id: number;
  name: string;
  trigger: string;
}

@Component({
  selector: 'app-event-settings-email',
  templateUrl: './event-settings-email.component.html',
  styleUrls: ['./event-settings-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventSettingsEmailComponent extends EmailTemplatesEditDirective implements OnInit, OnDestroy, HasUnsavedChanges {
  event: Event;

  paramTemplate: string;
  emailExists = false;

  emailTriggers: Array<SelectOption>;
  dataSource: MatTableDataSource<TriggerData>;
  triggerDataList: Array<TriggerData>;
  users: Array<UserNotification>;

  columns = ['trigger'];

  // Estado da matriz em MAPAS PLANOS (não FormGroup): leitura O(1) no template, sem
  // chamadas de função / `.get()` por célula a cada change detection, e sem uma
  // diretiva NgModel por checkbox (render mais leve). `cellValues[userId-triggerId]`.
  cellValues: { [name: string]: boolean } = {};
  // Estado memoizado do "marcar todos" por usuário (recalculado só nos toggles).
  masterChecked: { [userId: number]: boolean } = {};
  masterIndeterminate: { [userId: number]: boolean } = {};

  // ---- Staging das edições da matriz (confirmar/descartar em lote) ----
  // Nada é persistido até "Confirmar". `baselineCells` = estado salvo (do servidor);
  // `addedUserIds` = colunas adicionadas localmente; `pendingChanges` controla a barra.
  loading = true;
  pendingChanges = false;
  confirming = false;
  private baselineCells: { [name: string]: boolean } = {};
  private addedUserIds = new Set<number>();

  // ---- Fades de scroll do card (mesma ideia do Listar submissões) ----
  // Classes no scroller (.cochair-notification) que ligam: a sombra translúcida
  // abaixo do cabeçalho (scrolled-down) e os fades laterais (scrolled-left/right).
  scrolledLeft = false;
  scrolledRight = false;
  scrolledDown = false;
  private matrixScrollEl: HTMLElement;

  // ─── Busca inline de destinatário (perf/UX) ────────────────────────
  // Antes o botão "+" abria um MatDialog cheio com busca de usuário e
  // botão de confirmar. Substituído por um popover leve com `app-user-search`
  // ancorado no próprio "+" da linha: o usuário seleciona e a coluna é
  // adicionada na hora (a lógica de `stageAddUser` continua idêntica).
  addUserOpen = false;
  // CDK 11 `[cdkConnectedOverlayOrigin]` lê `origin.elementRef` — precisa de
  // um objeto duck-typed como `CdkOverlayOrigin`. HTMLElement direto quebra
  // silenciosamente (posicionamento vai pra 0,0). Envolvemos em `{elementRef}`.
  addUserAnchor: { elementRef: ElementRef<HTMLElement> } | null = null;
  addUserTrigger: TriggerData | null = null;
  addUserForm: FormGroup;
  addUserPositions: ConnectedPosition[] = [
    { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top',    offsetY: 6 },
    { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
  ];

  // Setter do scroller (vive sob *ngIf): ao aparecer, calcula o estado inicial dos
  // fades (ex.: tabela já mais larga que o card → fade direito).
  @ViewChild('matrixScroll') set matrixScroll(ref: ElementRef<HTMLElement>) {
    this.matrixScrollEl = ref ? ref.nativeElement : null;
    if (this.matrixScrollEl) { setTimeout(() => this.updateMatrixFades()); }
  }

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    this.sort = sort;
    if (this.sort) {
      this.dataSource.sort = this.sort;

      this.dataSource.sortingDataAccessor = (item: TriggerData, header: string) => {
        switch (header) {
          case 'trigger': return item.name;
          case 'user': return item.name;
          default: return item[header]; // Default is status columns
        }
      };
    }
  }
  sort: MatSort;

  constructor(
    protected fb: FormBuilder,
    protected translateService: TranslateService,
    protected eventsService: EventsService,
    private adminService: AdminService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super(true, fb, translateService, eventsService);
  }

  ngOnInit() {
    // FormGroup do popover inline de busca de usuário. Um só p/ toda a matriz
    // — quem muda a cada abertura é o `addUserAnchor`/`addUserTrigger`.
    this.addUserForm = this.fb.group({ user: ['', Validators.required] });

    // O card é o scroller vertical (max-height por modo de layout no SCSS via
    // :host-context). Bloqueamos o scroll do <html> enquanto a tela está montada p/
    // ele ser o único scroller (sem scroll chaining). Restaurado no destroy. Ver
    // reference_admin_scroll_container.
    document.documentElement.style.overflowY = 'hidden';

    setTimeout(() => {
      this.adminService.progressBar.start();
      this.route.queryParams.subscribe(({ template }) => {
        if (template) {
          this.paramTemplate = template;
        }
      });

      this.event = this.adminService.selectedEvent;
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.initComponent();
        });
      } else {
        this.initComponent();
      }
    });
  }

  ngOnDestroy(): void {
    // Restaura o scroll vertical do documento ao sair da tela.
    document.documentElement.style.overflowY = '';
  }

  initComponent(): void {
    this.initForm();
    this.getEventNotifications();
    this.parameterTemplate();
    // OnPush: initComponent roda dentro de setTimeout/subscribe (fora de evento de
    // template) → marca p/ exibir o main-body + placeholder de carregamento já.
    this.cdr.markForCheck();
  }

  // Opens a dialog to manage the import of templates from other events
  openTemplatesImportDialog(){
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.autoFocus = false;
    dialogConfig.maxHeight = '90vh'; // prevents dialog from exceeding 90% of viewport 
    dialogConfig.panelClass = 'templates-dialog';
    
    const dialogRef = this.dialog.open(ImportEmailTemplatesDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(data => {
      const triggerList = data.triggerList.filter(trigger => trigger.import).map(info => info.trigger);
      if(triggerList.length){
        this.importTemplates(data.eventId, triggerList);
      }
    });
  }

  // For every trigger chosen, copy them from the given event
  public importTemplates(eventId: number, triggerList){
    this.adminService.progressBar.start();
    this.eventsService.copyBulkEventNotificationEmail(this.event.id, triggerList, eventId).subscribe(response=> {
      this.adminService.progressBar.stop();
      this.notificationService.notify('admin.email-template.emails-imported');
      this.getEventNotifications();
    });
  }

  // Gets the notifications to load the table (data source).
  //   showProgress=true  → estado de carga inicial: liga `loading` (placeholder) +
  //                        barra de progresso do topo.
  //   showProgress=false → reload SILENCIOSO (ex.: pós-Confirmar): não toca em
  //                        `loading` nem na barra de topo; `done` roda ao concluir.
  private getEventNotifications(showProgress = true, done?: () => void) {
    if (showProgress) {
      this.loading = true;
      this.adminService.progressBar.start();
    }

    this.eventsService.getEventCoChairsNotification(this.event.id).subscribe(data => {
      // Só os gatilhos da lista (chair); formata.
      this.triggerDataList = data["triggers"].filter(t =>
        EmailTrigger.toIterable().includes(t["trigger"])
      ).map(trigger => ({
            id: trigger["id"],
            name: EmailTrigger.getName(trigger["trigger"]),
            trigger: trigger["trigger"]
      }));

      this.dataSource = new MatTableDataSource<TriggerData>(this.triggerDataList);
      this.users = data["users"].map(user => plainToClass(UserNotification, user));

      // Estado das células em mapa plano + master memoizado por usuário.
      this.cellValues = {};
      this.users.forEach(u => this.triggerDataList.forEach(t => {
        this.cellValues[`${u.id}-${t.id}`] = u.includeTrigger(t.id);
      }));
      this.users.forEach(u => this.refreshMaster(u.id));

      this.columns = ['trigger', ...this.users.map(u => 'user_' + u.id.toString()), 'addUser'];

      // Estado salvo (do servidor) vira o novo baseline; zera pendências.
      this.captureBaseline();
      if (showProgress) {
        this.loading = false;
        this.adminService.progressBar.stop();
      }
      if (done) { done(); }
      this.cdr.markForCheck();
    }, () => {
      if (showProgress) {
        this.loading = false;
        this.adminService.progressBar.stop();
      }
      if (done) { done(); }
      this.cdr.markForCheck();
    });
  }

  public parameterTemplate(): void {
    setTimeout(() => {
      // If parameter template is defined, test if valid.
      // In which case, set emailTrigger value and execute email selection change method.
      if (this.paramTemplate) {
        const validParamTemplate = this.emailTriggers.find(trigger => trigger.id === this.paramTemplate);

        if (validParamTemplate) {
          const emailTriggerControl = this.filterForm.controls['emailTrigger'];

          emailTriggerControl.setValue(this.paramTemplate);
          this.getEmailTemplate(emailTriggerControl.value);
        }
      }
    });
  }

  // Clicar no gatilho abre o modal de edição (único editor desde a remoção do
  // editor inline). Seta `emailTrigger` apenas p/ o getSelectedTriggerLabel()
  // resolver o rótulo passado ao modal.
  private selectTrigger(trigger: EmailTrigger): void {
    this.filterForm.get('emailTrigger')?.setValue(trigger);
    this.openEditDialog(trigger);
  }

  // Abre a edição do template numa janela modal. Saída só por Salvar/Descartar
  // (disableClose). Ao fechar com "saved", recarrega a matriz (destinatários
  // auto-adicionados podem ter mudado).
  private openEditDialog(trigger: EmailTrigger): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.width = '900px';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.maxHeight = '90vh';
    dialogConfig.panelClass = 'email-template-edit-dialog-panel';
    dialogConfig.data = {
      event: this.event,
      trigger,
      triggerLabel: this.getSelectedTriggerLabel()
    };

    this.dialog.open(EmailTemplateEditDialogComponent, dialogConfig)
      .afterClosed().subscribe(result => {
        if (result === 'saved') {
          this.getEventNotifications();
        }
      });
  }

  getSelectedTriggerLabel(): string {
    if (this.filterForm.get('emailTrigger').value != null && this.triggerDataList.length > 0) {
      return this.triggerDataList.filter(t => t.trigger == this.filterForm.get('emailTrigger').value)[0]?.name;
    }
    return null;
  }

  // Load from the back-end the infos for this specific template
  getEmailTemplate(trigger: EmailTrigger, onlyUpdateTo: boolean=false): void {    
    this.eventsService.getEventNotificationEmailbyEvent(trigger, this.event.id)
      .subscribe(nf => {
        if (onlyUpdateTo) {
          this.notificationEmail.to = nf.to;
        } else {
          this.changeType = true;
          this.notificationEmail = nf;
          this.emailExists = true;
          this.filterForm.patchValue({
            emailSubject: nf.subject,
            emailTextForm: nf.content
          });
        }
      }, () => {
        this.getDefaultEmailTemplate(trigger);
      });
  }

  // Update the information for the selected email template
  updateSelectedEmailTemplate(): void {
    if (this.emailExists) {
      this.eventsService.editEventNotificationEmail(this.event.id, this.notificationEmail.trigger, this.notificationEmail)
        .subscribe(nf => {
          this.notificationService.notify('admin.email-template.email-updated');
          this.notificationEmail = nf;
        });
    } else {
      this.eventsService.createEventNotificationEmail(this.event.id, this.notificationEmail)
        .subscribe(nf => {
          this.notificationEmail = nf;
          this.emailExists = true;
        });
    }
  }

  // trackBy do mat-table: estabiliza as linhas (gatilhos) entre renders.
  trackTrigger(_index: number, t: TriggerData): number {
    return t.id;
  }

  // (scroll) do card → recalcula os estados dos fades (sombra do topo + laterais).
  onMatrixScroll(el: HTMLElement): void {
    this.updateMatrixFades(el);
  }

  // Em resize, reavalia só os fades (a altura do card é puro CSS por modo de layout
  // via :host-context — reage sozinho a resize/troca de modo, sem medição JS, que
  // falhava no clássico porque a toolbar aparece DEPOIS da medição).
  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMatrixFades();
  }

  private updateMatrixFades(el: HTMLElement = this.matrixScrollEl): void {
    if (!el) { return; }
    this.scrolledDown = el.scrollTop > 0;
    this.scrolledLeft = el.scrollLeft > 0;
    // Há colunas escondidas à direita = ainda não rolou até o fim horizontal.
    this.scrolledRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    this.cdr.markForCheck();
  }

  // Conta gatilhos marcados de um usuário (sobre o mapa plano).
  private trueCount(userId: number): number {
    let n = 0;
    for (const t of this.triggerDataList) {
      if (this.cellValues[`${userId}-${t.id}`]) { n++; }
    }
    return n;
  }

  // Recalcula (memoiza) o estado do "marcar todos" do usuário. Chamado só nos
  // toggles/carga — o template lê os mapas direto (sem função por CD).
  private refreshMaster(userId: number): void {
    const total = this.triggerDataList.length;
    const count = this.trueCount(userId);
    this.masterChecked[userId] = total > 0 && count === total;
    this.masterIndeterminate[userId] = count > 0 && count < total;
  }

  // Marcar/desmarcar uma célula: atualiza o mapa + master memoizado + pendências.
  // Persiste apenas no "Confirmar". (Evento de template → OnPush dispara CD.)
  onCellToggle(user: UserNotification, triggerId: number, value: boolean): void {
    this.cellValues[`${user.id}-${triggerId}`] = value;
    this.refreshMaster(user.id);
    this.recomputePending();
  }

  // Master "marcar todos" da coluna: alterna todas as células do usuário (alvo =
  // inverso do estado memoizado). Só local; persiste no "Confirmar".
  onMasterToggle(user: UserNotification): void {
    const newValue = !this.masterChecked[user.id];
    this.triggerDataList.forEach(t => { this.cellValues[`${user.id}-${t.id}`] = newValue; });
    this.refreshMaster(user.id);
    this.recomputePending();
  }

  // Adicionar usuário (botão +): abre um popover leve com `app-user-search`
  // ancorado no próprio "+" da linha (substitui o MatDialog cheio anterior).
  // A seleção passa direto pra `stageAddUser` — o usuário nem precisa
  // confirmar; a coluna aparece na tabela e a barra floating oferece
  // "Confirmar/Descartar" pra persistir/descartar em lote como antes.
  openAddUser(trigger: TriggerData, anchor: HTMLElement): void {
    this.addUserTrigger = trigger;
    this.addUserAnchor = { elementRef: new ElementRef(anchor) };
    this.addUserOpen = true;
  }

  closeAddUser(): void {
    this.addUserOpen = false;
    this.addUserTrigger = null;
    this.addUserAnchor = null;
    this.addUserForm?.get('user')?.setValue([]);
  }

  onAddUserPicked(picked: User): void {
    const trigger = this.addUserTrigger;
    this.closeAddUser();
    if (!picked || !trigger) { return; }
    // `UserNotification` estende `User` (mesmos campos); cast direto — a
    // lógica de `stageAddUser` só usa `id` e `name` do `picked`.
    this.stageAddUser(picked as unknown as UserNotification, trigger.id);
  }

  // Foca o input do popover ao abrir — cursor já pronto pra digitar.
  // Precisa esperar 2 frames: (1) o overlay attach dispara ANTES do Angular
  // renderizar o `app-user-search` interno; (2) mat-form-field projeta o
  // `<input>` no frame seguinte. rAF-duplo é robusto sem timing arbitrário.
  focusAddUserInput(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const input = document.querySelector('.add-user-popover input') as HTMLInputElement;
        if (input) { input.focus(); input.select?.(); }
      });
    });
  }

  // Insere a coluna do usuário em memória: se já existe, só marca o gatilho clicado;
  // senão cria os controles (todos false, exceto o gatilho clicado) e a coluna.
  private stageAddUser(user: UserNotification, triggerId: number): void {
    const existing = this.users.find(u => u.id === user.id);
    if (existing) {
      this.cellValues[`${existing.id}-${triggerId}`] = true;
      this.refreshMaster(existing.id);
      this.recomputePending();
      this.cdr.markForCheck();
      return;
    }

    this.users.push(user);
    this.addedUserIds.add(user.id);
    this.triggerDataList.forEach(t => {
      this.cellValues[`${user.id}-${t.id}`] = t.id === triggerId;
    });
    this.refreshMaster(user.id);

    // Insere a coluna antes de 'addUser' (nova referência p/ o mat-table reagir).
    const insertAt = this.columns.length - 1;
    this.columns = [
      ...this.columns.slice(0, insertAt),
      'user_' + user.id.toString(),
      ...this.columns.slice(insertAt)
    ];
    this.recomputePending();
    this.cdr.markForCheck();
  }

  // Confirmar: persiste só os usuários alterados (PATCH por usuário com a lista
  // completa de gatilhos). Um não-chair que ficou com tudo desmarcado é enviado
  // all-false → o backend o remove (some no reload). Recarrega ao final.
  confirmChanges(): void {
    const affected = this.users.filter(u => this.isUserDirty(u));
    if (!affected.length) { this.pendingChanges = false; return; }

    // Spinner do botão Confirmar = ÚNICO feedback: sem barra de progresso do topo
    // e sem toast de sucesso.
    this.confirming = true;
    const requests = affected.map(u => this.eventsService.editEventCoChairsNotification(this.event.id, {
      user: {
        id: u.id,
        triggers: this.triggerDataList.map(t => ({
          id: t.id,
          value: !!this.cellValues[`${u.id}-${t.id}`]
        }))
      }
    }));

    forkJoin(requests).subscribe(() => {
      // Reload SILENCIOSO (sem barra de topo nem loading-placeholder) p/ a barra de
      // ações seguir visível com o spinner até concluir; `confirming` desliga no fim.
      this.getEventNotifications(false, () => { this.confirming = false; });
    }, () => {
      this.confirming = false;
      this.cdr.markForCheck();
    });
  }

  // Descartar: nada foi persistido → recarregar do servidor restaura o estado salvo
  // (marcações revertidas, colunas adicionadas somem, removidas voltam).
  discardChanges(): void {
    this.getEventNotifications();
  }

  // CanDeactivate: bloqueia sair da página (com diálogo de confirmação) enquanto
  // houver marcações/colunas pendentes não salvas na matriz.
  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  private isUserDirty(user: UserNotification): boolean {
    if (this.addedUserIds.has(user.id)) { return true; }
    return this.triggerDataList.some(t => {
      const name = `${user.id}-${t.id}`;
      return !!this.cellValues[name] !== this.baselineCells[name];
    });
  }

  private recomputePending(): void {
    this.pendingChanges = this.users.some(u => this.isUserDirty(u));
  }

  // Snapshot do estado salvo (após carregar/confirmar) p/ medir pendências.
  private captureBaseline(): void {
    this.baselineCells = {};
    this.users.forEach(u => this.triggerDataList.forEach(t => {
      const name = `${u.id}-${t.id}`;
      this.baselineCells[name] = !!this.cellValues[name];
    }));
    this.addedUserIds = new Set<number>();
    this.pendingChanges = false;
  }

}
